import Loading from 'core-system/Loading'
import React, { useCallback, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { AppState } from 'redux/config/store'
import {
  MyCommuter,
  MyCommuterArchived,
  MyCommuterDeclined,
  Commuter,
} from 'redux/employee/employeeTypes'
import { Segment } from 'redux/employer/employerTypes'
import PageHeader from '../components/PageHeader'
import PageBodyContainer from '../components/PageBodyContainer'
import InviteUsersModal from '../components/InviteUsersModal'
import UploadDataModal from '../components/UploadDataModal'
import CommuterTableOperations from './CommuterTableOperations'
import CommutersTable from './CommutersTable'
import styled from 'styled-components'
import palette from 'core-system/Themes/palette'
import {
  TableManipulationTopContainer,
  TableManipulationItem,
} from 'core-system/Table'
import MyCommutersArchived from './MyCommutersArchived'
import MyCommutersDeclined from './MyCommutersDeclined'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import MyCommutersPending from './MyCommutersPending'
import MyCommutersBlocked from './MyCommutersBlocked'

const HeaderSpan = styled(Text)`
  margin-left: 0.5rem;
`

const TableContainer = styled(TableManipulationTopContainer)`
  margin-bottom: 0 !important;
  border: 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    border: 1px solid ${({ theme }) => theme.palette.grey.grey300};
    padding: 1rem;
  }
`

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`

const getHeaderColor = (active: boolean, disabled: boolean) => {
  if (active) {
    return palette.white
  } else if (disabled) {
    return palette.text.disabled
  } else {
    return palette.secondary.purple700
  }
}

const HeaderItem = ({
  text,
  isDisabled,
  isActive,
  numCommuters,
  onClick,
}: {
  text: string
  isDisabled: boolean
  isActive: boolean
  numCommuters: number
  onClick: () => void
}) => {
  return (
    <TableManipulationItem
      onClick={onClick}
      active={isActive}
      disabled={isDisabled}
    >
      {numCommuters}
      <HeaderSpan
        variant='action4'
        color={getHeaderColor(isActive, isDisabled)}
      >
        {text}
      </HeaderSpan>
    </TableManipulationItem>
  )
}

interface MyCommutersView {
  myCommuters: MyCommuter[]
  activeCommuters: MyCommuter[]
  archivedEmployees: MyCommuterArchived[]
  blockedEmployees: MyCommuterArchived[]
  declinedEmployees: MyCommuterDeclined[]
  pendingEmployees: Commuter[]
  activeSegment: Segment
  activeTab: number
  setActiveTab: (tab: number) => void
  allEmployeeSegmentId: string
  tabs: { label: string; value: number }[]
}

const MyCommutersView = React.memo((props: MyCommutersView) => {
  const {
    myCommuters,
    activeCommuters,
    archivedEmployees,
    blockedEmployees,
    declinedEmployees,
    pendingEmployees,
    activeSegment,
    activeTab,
    setActiveTab,
    allEmployeeSegmentId,
    tabs,
  } = props

  const [activeTable, setActiveTable] = useState('all')

  const commutersForOperations = () => {
    if (activeTable === 'all') {
      return myCommuters
    } else if (activeTable === 'pending') {
      return pendingEmployees
    } else if (activeTable === 'archived') {
      return archivedEmployees
    } else if (activeTable === 'blocked') {
      return blockedEmployees
    } else if (activeTable === 'declined') {
      return declinedEmployees
    }
  }

  const { segmentsMap } = useSelector((state: AppState) => state.employer)
  const hrisStatus = useSelector((state: AppState) => ({
    providerName: state.employer.profile?.payrollProviderId,
    reauth: state.employer.profile?.finchReauth,
  }))
  const apiRosterSyncEnabled = useSelector((state: AppState) =>
    Boolean(state.employer.profile?.apiRosterSyncEnabled)
  )

  const [filteredCommuters, setFilteredCommuters] = useState(
    commutersForOperations()
  )

  useEffect(() => {
    if (myCommuters) {
      setFilteredCommuters(commutersForOperations())
    }
  }, [myCommuters])

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadSegment, setUploadSegment] = useState(null)

  const [showInviteUsersModal, setShowInviteUsersModal] = useState(false)

  const toggleInviteUsersModal = React.useCallback(() => {
    setShowInviteUsersModal(!showInviteUsersModal)
    SegmentService.track('inviteUsers-modal-click', {
      action: showInviteUsersModal ? 'close' : 'open',
    })
  }, [showInviteUsersModal])

  const handleUploadModalOpen = useCallback(
    (segment?: string) => {
      if (segment) {
        setUploadSegment(segmentsMap[segment])
      }
      setIsUploadModalOpen(true)
    },
    [segmentsMap]
  )

  const handleHeaderItemClick = (table: string, commuters: any[]) => {
    setActiveTable(table)
    setFilteredCommuters(commuters)
  }

  if (!myCommuters || !filteredCommuters || !activeCommuters) {
    return <Loading isCard={false} />
  }

  return (
    <>
      <PageHeader
        inviteCommutersCSV={handleUploadModalOpen}
        hrisStatus={hrisStatus}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        showAddCommutersButton={!apiRosterSyncEnabled}
        showInviteCommutersButton
        onInviteCommutersClick={toggleInviteUsersModal}
        inviteCommutersCSVDisabled={hrisStatus?.providerName ? true : false}
        inviteCommutersCSVTooltip={
          hrisStatus?.providerName
            ? 'You cannot import commuters via CSV while connected to a payroll provider through HRIS'
            : ''
        }
      />
      <InviteUsersModal
        segmentId={allEmployeeSegmentId}
        closeModal={toggleInviteUsersModal}
        open={showInviteUsersModal}
      />
      <PageBodyContainer>
        <TopContainer>
          <TableContainer>
            <HeaderItem
              text='All'
              isDisabled={!myCommuters || myCommuters.length === 0}
              isActive={activeTable === 'all'}
              numCommuters={1736}
              onClick={() => handleHeaderItemClick('all', myCommuters)}
            />
            <HeaderItem
              text='Pending'
              isDisabled={false}
              isActive={activeTable === 'pending'}
              numCommuters={382}
              onClick={() => handleHeaderItemClick('pending', pendingEmployees)}
            />
            <HeaderItem
              text='Archived'
              isDisabled={false}
              isActive={activeTable === 'archived'}
              numCommuters={102}
              onClick={() =>
                handleHeaderItemClick('archived', archivedEmployees)
              }
            />
            <HeaderItem
              text='Blocked'
              isDisabled={!blockedEmployees || blockedEmployees.length === 0}
              isActive={activeTable === 'blocked'}
              numCommuters={blockedEmployees.length}
              onClick={() =>
                handleHeaderItemClick('blocked', blockedEmployees)
              }
            />
            <HeaderItem
              text='Declined'
              isDisabled={false}
              isActive={activeTable === 'declined'}
              numCommuters={7}
              onClick={() =>
                handleHeaderItemClick('declined', declinedEmployees)
              }
            />
          </TableContainer>
          <CommuterTableOperations
            commuters={commutersForOperations()}
            setFilteredCommuters={setFilteredCommuters}
          />
        </TopContainer>
        {activeTable === 'all' && (
          <CommutersTable
            myCommuters={filteredCommuters}
            activeCommuters={activeCommuters}
            activeSegment={activeSegment}
            showCommuterTableOperations={false}
            isEdit={false}
            isAdd={false}
            resetPaginationOnRowsChange
          />
        )}
        {activeTable === 'archived' && (
          <MyCommutersArchived
            archivedEmployees={filteredCommuters}
            activeSegment={allEmployeeSegmentId}
          />
        )}
        {activeTable === 'blocked' && (
          <MyCommutersBlocked blockedEmployees={filteredCommuters} />
        )}
        {activeTable === 'declined' && (
          <MyCommutersDeclined declinedEmployees={filteredCommuters} />
        )}
        {activeTable === 'pending' && (
          <>
            <FlexContainer
              justifyContent='space-between'
              alignItems='center'
              marginBottom='1.5rem'
            >
              <Text variant='body1'>
                These commuters have signed up but aren’t part of any groups
                yet. Select and approve the commuters you authorize to be part
                of the platform. Once approved they will be part of{' '}
                <strong>{segmentsMap[allEmployeeSegmentId].name}</strong>. Any
                declined commuters will be removed.
              </Text>
            </FlexContainer>
            <MyCommutersPending
              pendingCommuters={filteredCommuters}
              activeSegment={allEmployeeSegmentId}
            />
          </>
        )}
      </PageBodyContainer>
      <UploadDataModal
        open={isUploadModalOpen}
        closeModal={() => setIsUploadModalOpen(false)}
        segment={uploadSegment}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommutersView.displayName = 'MyCommutersView'
}

export default MyCommutersView
